import axios from 'axios';
import { ADMIN_VIDEOS_ROUTE } from '../../api/backend';
import { Paginated } from '../../common/types';
import {
  AdminVideo,
  UploadUrl,
  VideoUpload,
} from '../../common/model/dto/video/admin-video';
import { AdminVideoQuery } from '../../common/model/dto/video/admin-video-query';
import { VideoPlacement } from '../../common/model/dto/video/video-placement';
import {
  CreatePosterUploadRequest,
  CreateVideoPlacementRequest,
  CreateVideoRequest,
  ReplaceVideoRequest,
  UpdateVideoPlacementRequest,
  UpdateVideoRequest,
} from '../../common/model/dto/video/video-requests';

const placementRoute = (key: string) =>
  `${ADMIN_VIDEOS_ROUTE}/placements/${encodeURIComponent(key)}`;

export const getVideoPlacements = async (): Promise<VideoPlacement[]> => {
  const { data } = await axios.get<VideoPlacement[]>(
    `${ADMIN_VIDEOS_ROUTE}/placements`
  );

  return data;
};

export const createVideoPlacement = async (
  request: CreateVideoPlacementRequest
): Promise<VideoPlacement> => {
  const { data } = await axios.post<VideoPlacement>(
    `${ADMIN_VIDEOS_ROUTE}/placements`,
    request
  );

  return data;
};

export const updateVideoPlacement = async (
  key: string,
  request: UpdateVideoPlacementRequest
): Promise<VideoPlacement> => {
  const { data } = await axios.patch<VideoPlacement>(
    placementRoute(key),
    request
  );

  return data;
};

export const deleteVideoPlacement = async (key: string): Promise<void> => {
  await axios.delete(placementRoute(key));
};

export const getVideos = async (
  params: AdminVideoQuery
): Promise<Paginated<AdminVideo>> => {
  const { data } = await axios.get<Paginated<AdminVideo>>(ADMIN_VIDEOS_ROUTE, {
    params,
  });

  return data;
};

export const getVideo = async (id: string): Promise<AdminVideo> => {
  const { data } = await axios.get<AdminVideo>(`${ADMIN_VIDEOS_ROUTE}/${id}`);

  return data;
};

export const createVideo = async (
  request: CreateVideoRequest
): Promise<VideoUpload> => {
  const { data } = await axios.post<VideoUpload>(ADMIN_VIDEOS_ROUTE, request);

  return data;
};

export const createVideos = async (
  videos: CreateVideoRequest[]
): Promise<VideoUpload[]> => {
  const { data } = await axios.post<VideoUpload[]>(
    `${ADMIN_VIDEOS_ROUTE}/bulk`,
    { videos }
  );

  return data;
};

export const replaceVideo = async (
  id: string,
  request: ReplaceVideoRequest
): Promise<VideoUpload> => {
  const { data } = await axios.post<VideoUpload>(
    `${ADMIN_VIDEOS_ROUTE}/${id}/replace`,
    request
  );

  return data;
};

export const completeVideoUpload = async (id: string): Promise<AdminVideo> => {
  const { data } = await axios.post<AdminVideo>(
    `${ADMIN_VIDEOS_ROUTE}/${id}/upload-complete`
  );

  return data;
};

export const retryVideo = async (id: string): Promise<AdminVideo> => {
  const { data } = await axios.post<AdminVideo>(
    `${ADMIN_VIDEOS_ROUTE}/${id}/retry`
  );

  return data;
};

export const updateVideo = async (
  id: string,
  request: UpdateVideoRequest
): Promise<AdminVideo> => {
  const { data } = await axios.patch<AdminVideo>(
    `${ADMIN_VIDEOS_ROUTE}/${id}`,
    request
  );

  return data;
};

export const createPosterUpload = async (
  id: string,
  request: CreatePosterUploadRequest
): Promise<UploadUrl> => {
  const { data } = await axios.post<UploadUrl>(
    `${ADMIN_VIDEOS_ROUTE}/${id}/poster`,
    request
  );

  return data;
};

export const completePosterUpload = async (id: string): Promise<AdminVideo> => {
  const { data } = await axios.post<AdminVideo>(
    `${ADMIN_VIDEOS_ROUTE}/${id}/poster/upload-complete`
  );

  return data;
};

export const deleteVideo = async (id: string): Promise<void> => {
  await axios.delete(`${ADMIN_VIDEOS_ROUTE}/${id}`);
};
